import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Custom hook for teaser edit functionality
 * Allows users to edit one word before redirecting to payment portal
 * @param {boolean} isSubscriptionActive - Whether user has active subscription
 * @returns {object} - Edit handlers and state
 */
export const useTeaserEdit = (isSubscriptionActive = false) => {
  const navigate = useNavigate();
  const [editingField, setEditingField] = useState(null);
  const [showRedirectModal, setShowRedirectModal] = useState(false);
  const originalValues = useRef({});
  const wordCount = useRef(0);

  const startEdit = (fieldId, currentValue) => {
    if (isSubscriptionActive) return;
    
    setEditingField(fieldId);
    originalValues.current[fieldId] = currentValue;
    wordCount.current = 0;
  };

  const handleInput = (e, fieldId) => {
    if (isSubscriptionActive || !editingField) return;

    const newValue = e?.target?.textContent || e?.target?.value || '';
    const originalValue = originalValues?.current?.[fieldId] || '';
    
    // Count words changed
    const originalWords = originalValue?.trim()?.split(/\s+/);
    const newWords = newValue?.trim()?.split(/\s+/);
    
    let changedWords = 0;
    for (let i = 0; i < Math.max(originalWords?.length, newWords?.length); i++) {
      if (originalWords?.[i] !== newWords?.[i]) {
        changedWords++;
      }
    }

    // If one word has been changed, show modal and redirect
    if (changedWords >= 1 && wordCount?.current === 0) {
      wordCount.current = changedWords;
      setShowRedirectModal(true);
      
      // Redirect after showing message
      setTimeout(() => {
        navigate('/payment-contact');
      }, 2000);
    }
  };

  const closeModal = () => {
    setShowRedirectModal(false);
  };

  const resetEdit = () => {
    setEditingField(null);
    originalValues.current = {};
    wordCount.current = 0;
  };

  return {
    editingField,
    showRedirectModal,
    startEdit,
    handleInput,
    closeModal,
    resetEdit,
    isEditable: !isSubscriptionActive
  };
};

export default useTeaserEdit;